// ---------- ce que les trois écrans partagent ----------
// Les dessins (logo, étoile, icônes), la barre du haut, le fond du thème, et le passage d'un
// écran à l'autre. Le jeu est en DOM sans framework : chaque écran se construit en une chaîne
// HTML, puis ne bascule plus que des classes — reconstruire sous les doigts du joueur coupe les
// animations en plein vol (cf. CLAUDE.md, « Le DOM »).

import { checkDecor } from '../shared/decor';
import type { Theme } from '../shared/types';
import { t } from './i18n';

export const $ = (id: string): HTMLElement => document.getElementById(id)!;

export const esc = (s: string): string =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );

/** Le logo : une grille de deux sur deux, la case jaune — celle du curseur — porte le V. */
export const LOGO =
  '<svg class="logo" viewBox="0 0 32 32" aria-hidden="true"><rect x="1" y="1" width="14" height="14" rx="4" fill="#ffe066"/><rect x="17" y="1" width="14" height="14" rx="4" fill="#fff8ea"/><rect x="1" y="17" width="14" height="14" rx="4" fill="#fff8ea"/><rect x="17" y="17" width="14" height="14" rx="4" fill="#fff8ea" opacity=".55"/><path d="M4.5 4.5l3.5 7 3.5-7" fill="none" stroke="#5530d1" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

export const STAR =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2.8l2.8 5.8 6.3.9-4.6 4.4 1.1 6.3L12 17.2l-5.6 3 1.1-6.3L2.9 9.5l6.3-.9z"/></svg>';

const stroke = (d: string, w = 2.6): string =>
  `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;

export const ICON = {
  close: stroke('<path d="M6 6l12 12M18 6L6 18"/>'),
  prev: stroke('<path d="M15 6l-6 6 6 6"/>', 2.8),
  next: stroke('<path d="M9 6l6 6-6 6"/>', 2.8),
  play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 4.5v15l12.5-7.5z"/></svg>',
  eye: stroke(
    '<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
    2.4,
  ),
  erase: stroke('<path d="M21 5H9l-6 7 6 7h12z"/><path d="M12.5 9.5l5 5M17.5 9.5l-5 5"/>', 2.2),
  lock: stroke(
    '<rect x="5" y="11" width="14" height="10" rx="2.5"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>',
    2.4,
  ),
  flag: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M5 22V3h2v1h12l-3 5 3 5H7v8z"/></svg>',
  crown:
    '<svg viewBox="0 0 30 22" aria-hidden="true"><path d="M3 20l-1-14 7 6 6-10 6 10 7-6-1 14z" fill="#ffd23f" stroke="#e2a000" stroke-width="1.5" stroke-linejoin="round"/></svg>',
  calendar: stroke(
    '<rect x="3.5" y="5" width="17" height="15" rx="3"/><path d="M3.5 10h17M8 3v4M16 3v4"/>',
    2.2,
  ),
  flame:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2c1 4 5 5.5 5 11a5 5 0 0 1-10 0c0-2.4 1-3.9 2.3-5.2.2 1.8 1 2.8 2.2 3.2C11 8 11 5 12 2z"/></svg>',
  gear: stroke(
    '<circle cx="12" cy="12" r="3.2"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>',
    2,
  ),
  share: stroke(
    '<circle cx="18" cy="5" r="2.5"/><circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="19" r="2.5"/><path d="M8.2 10.9l7.6-4.6M8.2 13.1l7.6 4.6"/>',
    2.2,
  ),
  path: stroke(
    '<circle cx="6" cy="18" r="2"/><circle cx="18" cy="6" r="2"/><path d="M8 18h5a3 3 0 0 0 0-6h-2a3 3 0 0 1 0-6h5"/>',
    2.4,
  ),
  /** Le compte (src/profile.ts) : une tête et des épaules, le pictogramme que tout le monde lit
   *  comme « mon profil » sans légende. */
};

/** La barre du haut, la même partout : le logo, le nom, une ligne de contexte, et à droite ce que
 *  l'écran y met (la croix de sortie en jeu, le compte d'étoiles à l'accueil). */
export const topBar = (line: string, right: string): string =>
  `<header class="topbar">${LOGO}<div class="brand"><b>Verbigrid</b><span>${esc(line)}</span></div>${right}</header>`;

/** La même forme que le Worker exige (backend/src/validation.ts, COLOR). Relue ici parce que la
 *  couleur part dans un attribut `style` : un thème servi par un cache, ou par un Worker d'une
 *  version qui ne la vérifiait pas encore, ne doit rien pouvoir y glisser d'autre qu'une couleur —
 *  le jeu relit déjà le décor de la même façon (checkDecor). */
const COLOR =
  /^(#[0-9a-fA-F]{3,4}|#[0-9a-fA-F]{6}|#[0-9a-fA-F]{8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\))$/;
export const safeColor = (v: string | undefined, fallback: string): string =>
  v && COLOR.test(v) ? v : fallback;

/** Le fond d'un thème : son dégradé sur <body>, et la barre système accordée à son haut. */
export function applyTheme(theme: Theme | undefined): void {
  const bg = theme
    ? `linear-gradient(180deg, ${theme.background.map((s) => `${safeColor(s.color, '#4b2bd6')} ${Number(s.at) || 0}%`).join(', ')})`
    : 'linear-gradient(160deg, #4b2bd6, #8a3ff0 55%, #ff6fae)';
  document.body.style.setProperty('--bg', bg);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', safeColor(theme?.background[0]?.color, '#4b2bd6'));
}

/** Le décor d'un thème, contrôlé une seconde fois ICI : le Worker l'a déjà passé à la liste
 *  blanche (shared/decor.ts), mais c'est du balisage injecté dans la page, et une copie hors ligne
 *  ancienne ou altérée ne doit pas pouvoir y glisser autre chose. */
export function decorSvg(theme: Theme | undefined): string {
  if (!theme?.decor) return '';
  const d = checkDecor(theme.decor);
  return d.ok ? d.svg : '';
}

export type Screen = 'home' | 'play' | 'end' | 'settings';
const SCREENS: Screen[] = ['home', 'play', 'end', 'settings'];
export function show(screen: Screen): void {
  for (const s of SCREENS) $(s).hidden = s !== screen;
  $('state').hidden = true;
  scrollTo(0, 0);
}

/** L'écran d'attente ou d'erreur, avec un bouton pour réessayer — et, quand on y est arrivé
 *  depuis le chemin (une partie qui ne charge pas hors ligne), un bouton pour y retourner :
 *  sans lui, le joueur n'avait que « Réessayer » et restait enfermé tant que le réseau manquait. */
export function showState(
  kind: 'loading' | 'offline' | 'empty',
  retry?: () => void,
  back?: () => void,
): void {
  for (const s of SCREENS) $(s).hidden = true;
  const el = $('state');
  el.hidden = false;
  stateBack = back ?? null;
  el.innerHTML =
    `${LOGO}<p>${t(`state.${kind}`)}</p>` +
    (retry
      ? `<button class="btn btn-go" type="button" data-act="retry">${t('state.retry')}</button>`
      : '') +
    (back
      ? `<button class="btn btn-soft" type="button" data-act="back">${ICON.path}${t('end.path')}</button>`
      : '');
  el.querySelector('[data-act="retry"]')?.addEventListener('click', retry!);
  el.querySelector('[data-act="back"]')?.addEventListener('click', back!);
}

let stateBack: (() => void) | null = null;
/** Le retour possible depuis l'écran d'état affiché, s'il y en a un (le bouton Retour d'Android). */
export const stateExit = (): (() => void) | null => ($('state').hidden ? null : stateBack);

/** Un message bref par-dessus l'écran (« LOUP ! », « Mot mystère trouvé ! »). */
let toastTimer = 0;
export function toast(text: string): void {
  let el = document.querySelector<HTMLElement>('.toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('role', 'status');
    document.body.append(el);
  }
  el.textContent = text;
  el.classList.remove('on');
  void el.offsetWidth;
  el.classList.add('on');
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => el!.classList.remove('on'), 1600);
}
